/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from 'react';
import type {
  ReactElement,
  ReactNode,
} from 'react';

import type {
  GroupBase,
  Props as SelectProps,
} from 'react-select';

import { AsyncPaginate } from 'react-select-async-paginate';
import type {
  ComponentProps,
  UseAsyncPaginateParams,
} from 'react-select-async-paginate';

import { FilterSelectBase } from '../FilterSelectBase';

import { selectComponentsAsync } from '../ReactSelectExtended/filterComponents';
import type {
  OptionTypeBase,
  FilterSelectBaseProps,
} from '../ReactSelectExtended/types';

type SelectWithInputValueProps<
OptionType extends OptionTypeBase,
Group extends GroupBase<OptionType>,
Additional,
IsMulti extends boolean,
> =
  & SelectProps<OptionType, IsMulti, Group>
  & UseAsyncPaginateParams<OptionType, Group, Additional>
  & ComponentProps<OptionType, Group, IsMulti>;

function SelectWithInputValue<
OptionType extends OptionTypeBase,
Group extends GroupBase<OptionType>,
Additional,
IsMulti extends boolean,
>(
  props: SelectWithInputValueProps<OptionType, Group, Additional, IsMulti>,
): ReactElement {
  const [inputValue, setInputValue] = useState('');

  return (
    <AsyncPaginate
      {...props}
      inputValue={inputValue}
      onInputChange={setInputValue}
    />
  );
}

export type FilterSelectAsyncProps<
OptionType extends OptionTypeBase,
Group extends GroupBase<OptionType>,
Additional,
IsMulti extends boolean,
> =
  & Omit<FilterSelectBaseProps<OptionType, IsMulti, Group>, 'renderSelect'>
  & UseAsyncPaginateParams<OptionType, Group, Additional>
  & ComponentProps<OptionType, Group, IsMulti>;

export function FilterSelectAsync<
OptionType extends OptionTypeBase,
Group extends GroupBase<OptionType>,
Additional,
IsMulti extends boolean,
>(
  {
    selectRef,
    ...rest
  }: FilterSelectAsyncProps<OptionType, Group, Additional, IsMulti>,
): ReactElement {
  return (
    <FilterSelectBase
      {...rest}
      renderSelect={(selectRenderProps): ReactNode => (
        <SelectWithInputValue
          {...rest}
          defaultOptions
          {...selectRenderProps}
          components={selectComponentsAsync}
          selectRef={selectRef}
        />
      )}
    />
  );
}
